import { useTranslation } from '../../../../../i18n';
import { InventoryLocation } from '../../../../../types/api';

export interface FlatTreeOption {
  id: string;
  name: string;
  depth: number;
  typeLabel: string;
}

/** Walk the location tree recursively to produce a flat list with depth info for select dropdowns. */
export function buildFlatTreeOptions(
  locations: InventoryLocation[],
  excludeId?: string | null,
  t?: ReturnType<typeof useTranslation>['t']
): FlatTreeOption[] {
  const result: FlatTreeOption[] = [];
  const childrenOf = (parentId: string | null) =>
    locations
      .filter((l) => l.parent_id === parentId && l.id !== excludeId)
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));

  const walk = (parentId: string | null, depth: number) => {
    for (const loc of childrenOf(parentId)) {
      const typeLabel = t
        ? t(`inventory.locationTypes.${loc.location_type}` as Parameters<typeof t>[0])
        : loc.location_type;
      result.push({ id: loc.id, name: loc.name, depth, typeLabel });
      walk(loc.id, depth + 1);
    }
  };
  walk(null, 0);
  return result;
}

/** Build the full ancestor path from root down to the given location. */
export function buildLocationPath(
  locationId: string,
  locations: InventoryLocation[]
): InventoryLocation[] {
  const byId = new Map(locations.map((l) => [l.id, l]));
  const path: InventoryLocation[] = [];
  let current = byId.get(locationId);
  while (current) {
    path.unshift(current);
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return path;
}

/** Collect ancestor IDs for a set of location IDs. */
export function collectAncestorIds(
  locationIds: Set<string>,
  locations: InventoryLocation[]
): Set<string> {
  const byId = new Map(locations.map((l) => [l.id, l]));
  const ancestors = new Set<string>();
  for (const id of locationIds) {
    let current = byId.get(id);
    while (current?.parent_id) {
      ancestors.add(current.parent_id);
      current = byId.get(current.parent_id);
    }
  }
  return ancestors;
}
